import { useState } from 'react';
import { FormControl, FormLabel, Stack } from '@chakra-ui/react';
import { Field } from 'formik';
import { useSelector } from 'react-redux';
import FormikInput from 'components/forms/new/formikFormFields/NameField';
import FieldToolTip from 'components/forms/new/FieldToolTip';
import DatePicker from '@/components/forms/new/formikFormFields/DatePicker';
import {
    formLabelStyling,
    stackSpacing,
    stackStyling,
    descriptionStyling,
} from 'components/forms/new/formStyles';
import walkthroughIds from '../walkthroughIds';

// Find the UTC timestamp that equals midnight of (year, month, day) in ianaTimeZone
const getMidnightUTCInTimezone = (year, month, day, ianaTimeZone) => {
    const approx = new Date(Date.UTC(year, month, day, 0, 0, 0));
    const fmt = new Intl.DateTimeFormat('en-US', {
        timeZone: ianaTimeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
    });
    const p = {};
    fmt.formatToParts(approx).forEach(({ type, value }) => {
        p[type] = value;
    });
    const localHour = parseInt(p.hour) === 24 ? 0 : parseInt(p.hour);
    const localAsUTC = new Date(
        Date.UTC(
            parseInt(p.year),
            parseInt(p.month) - 1,
            parseInt(p.day),
            localHour,
            parseInt(p.minute),
            parseInt(p.second),
        ),
    );
    return new Date(
        approx.getTime() - (localAsUTC.getTime() - approx.getTime()),
    );
};

const KeyExpiryField = ({ darkMode, isEditMode = false }) => {
    const [isHovered, setIsHovered] = useState(false);
    const [keyExpiryDate, setKeyExpiryDate] = useState(null);
    const { timeZone } = useSelector((state) => state.settings);
    const ianaTimezone = timeZone?.timeZone || 'UTC';

    return (
        <FormControl
            sx={{ margin: '0 !important' }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}>
            <Stack direction="row" spacing={stackSpacing} sx={stackStyling()}>
                <FormLabel sx={formLabelStyling(darkMode)}>
                    API Key Expiry (UTC)
                </FormLabel>
                {isEditMode ? (
                    <Field
                        as={FormikInput}
                        id="keyExpiry"
                        name="keyExpiry"
                        isReadOnly
                        placeholder="Not set"
                        data-walkthroughid={
                            walkthroughIds.createMode.ufKeyExpiryInput
                        }
                        sx={{ color: 'gray.500', ...descriptionStyling }}
                    />
                ) : (
                    <Field name="keyExpiry">
                        {({ field, form }) => {
                            const dateValue = form.values.keyExpiry
                                ? new Date(form.values.keyExpiry)
                                : keyExpiryDate;
                            return (
                                <DatePicker
                                    field={field}
                                    form={form}
                                    darkMode={darkMode}
                                    date={dateValue}
                                    changeHandler={(_, value) => {
                                        if (
                                            value instanceof Date &&
                                            !isNaN(value)
                                        ) {
                                            const utcDate =
                                                getMidnightUTCInTimezone(
                                                    value.getFullYear(),
                                                    value.getMonth(),
                                                    value.getDate(),
                                                    ianaTimezone,
                                                );
                                            form.setFieldValue(
                                                'keyExpiry',
                                                utcDate.toISOString(),
                                            );
                                        } else {
                                            form.setFieldValue('keyExpiry', '');
                                        }
                                        setKeyExpiryDate(value);
                                    }}
                                    walkthroughid={
                                        walkthroughIds.createMode
                                            .ufKeyExpiryInput
                                    }
                                    isLoading={form.isSubmitting}
                                />
                            );
                        }}
                    </Field>
                )}
                {isHovered && (
                    <FieldToolTip
                        label={
                            isEditMode
                                ? 'Key expiry is set at creation and cannot be changed.'
                                : 'Select the date when the API key will expire.'
                        }
                        ariaLabel="service-provider-key-expiry-tip"
                        left="25px"
                        top="25px"
                    />
                )}
            </Stack>
        </FormControl>
    );
};

export default KeyExpiryField;
